import * as React from 'react';
import { styled } from '@glitz/react';
import { ProductListingTopImage } from 'webapp/components/BaseProductListingTopImage';
import { ArticleListing } from 'webapp/inspiration/article-listing';
import { isComponentType, currentUrl, useMatchMobile } from '@avensia/excite';
import { LinkListProvider } from 'webapp/blocks/LinkListBlock';
import { createMaxSmallViewportDecorator, createMinMediumViewportDecorator } from 'webapp/viewport';
import { translate } from '@avensia/excite';
import { headingDecorator } from 'webapp/decorators/heading';
import { BreadCrumbs } from 'webapp/components/Breadcrumbs';
import { ContentList } from '@avensia/excite-ninetailed';
import ProductListing from 'webapp/product-listing';
import { isSvenssons } from 'webapp/theme';
import { white } from 'webapp/shared/sharedTheme';
import { ProductListingTop } from 'webapp/components/BaseProductListingTop';
import AdditionalContent from 'webapp/product-listing/AdditionalContent';
import { ContentKeys, isContentCleaned } from 'webapp/utils/content-cleaner';
import { useContentScroll } from 'webapp/utils/content-scroll';
import RetailMediaBannerAd from 'webapp/components/retail-media/RetailMediaBannerAd';
import { useRetailMedia } from 'webapp/components/retail-media/use-retail-media';

type PageType = {
  id?: string;
  name: string;
  topContent: Excite.Contentful.ContentListApiModel;
  bottomContent: Excite.Contentful.ContentListApiModel;
  topSmallViewportImage?: Excite.ContentApi.ImageApiModel;
  topLargeViewportImage?: Excite.ContentApi.ImageApiModel;
  shortDescription?: string;
  opacity?: number;
  additionalContentTitle?: string;
  additionalContentDescription?: Excite.Contentful.DocumentApiModel;
  articles?: NordicNest.Api.ArticleListItemApiModel[];
  selectedFacets?: Excite.Esales.FacetApiModel[];
};

export default function BaseProductListingPage<T extends PageType>(
  props: NordicNest.Api.BaseProductListingPageApiModel<T> & {
    useRightTopImage?: boolean;
  },
) {
  const hasSubNavigation =
    isComponentType<NordicNest.Api.ProductListingPageApiModel>(props) ||
    isComponentType<NordicNest.Api.BrandPageApiModel>(props) ||
    isComponentType<NordicNest.Api.DesignerPageApiModel>(props) ||
    isComponentType<NordicNest.Api.CollectionPageApiModel>(props);

  const pageTrackingData = (props.page as unknown as NordicNest.Api.NNCFBasePage)?.trackingData;
  const showAdditionalContent = props.page?.additionalContentTitle || props.page?.additionalContentDescription;

  const selectedRangedFacets =
    props.facets?.filter(
      facet =>
        facet.isRange && (facet.range.maxSelected !== facet.range.max || facet.range.minSelected !== facet.range.min),
    ) || [];

  const retailMediaContent = useRetailMedia(
    'category',
    currentUrl().pathname,
    undefined,
    undefined,
    [...props.selectedFacets, ...selectedRangedFacets],
    props.retailMediaEnabled,
  );

  //remove all content when load page with pagination query to avoid duplicate content for seo
  const showCmsContent = props.pagination.currentPage <= 1;
  const showTopSubNavigation = hasSubNavigation && !isContentCleaned(ContentKeys.TopSubNavigationArea);
  const showTopContent = showCmsContent && !isContentCleaned(ContentKeys.TopContent);

  const { topContentRef, bottomContentRef } = useContentScroll<HTMLDivElement>();

  const isMobile = useMatchMobile();
  const topRetailMediaBanner = isMobile
    ? retailMediaContent?.mobileTopBanners[0] || retailMediaContent?.topBanners[0]
    : retailMediaContent?.topBanners[0];

  return (
    <Base>
      <BreadCrumbs {...props.breadcrumbs} />
      {props.useRightTopImage ? (
        <ProductListingTop
          heading={props.h1Title ?? props.page.name}
          shortDescription={props.page.shortDescription}
          topLargeViewportImage={props.page.topLargeViewportImage}
          topSmallViewportImage={props.page.topSmallViewportImage}
          showCmsContent={showCmsContent}
        />
      ) : (
        <ProductListingTopImage
          shortDescription={props.page.shortDescription}
          topLargeViewportImage={props.page.topLargeViewportImage}
          topSmallViewportImage={props.page.topSmallViewportImage}
          heading={props.h1Title ?? props.page.name}
          opacity={props.page.opacity}
          showCmsContent={showCmsContent}
        />
      )}
      {showTopSubNavigation && (
        <LinkListProvider>
          <ContentList {...props.page.topSubnavigationArea} />
        </LinkListProvider>
      )}
      {showTopContent && <ContentList {...props.page.topContent} />}
      <div ref={topContentRef} data-test-id="product-listing-ref" />
      {topRetailMediaBanner !== undefined && (
        <RetailMediaBannerAdHeader
          banner={topRetailMediaBanner}
          css={
            (showTopSubNavigation && props.page?.topSubnavigationArea?.items?.length > 0) ||
            (showTopContent && props.page?.topContent?.items?.length > 0)
              ? undefined
              : retailMediaBannerTopMarginDecorator
          }
        />
      )}
      <ProductListing
        id={pageTrackingData?.id ?? props.page?.id ?? 'product_listing_page_missing_id'}
        name={pageTrackingData?.internalName ?? props.page?.name ?? 'product_listing_page_missing_name'}
        categoryNavigationHeading={props.page.name}
        products={props.products}
        facets={props.facets}
        highlightedFacets={props.highlightedFacets}
        pagination={props.pagination}
        sortOptions={props.sortOptions}
        ads={props.ads}
        pageName={props.h1Title ?? props.page.name}
        subPageData={props.subPageData}
        selectedFacets={props.selectedFacets}
        defaultPaginationSize={props.defaultPaginationSize}
        retailMediaBannerAd={retailMediaContent?.productListBanners[0]}
        retailMediaSponsoredProducts={retailMediaContent?.sponsoredProducts}
      />
      {showAdditionalContent && showCmsContent && !isContentCleaned(ContentKeys.AdditionalContentDescription) && (
        <AdditionalContent
          title={props.page?.additionalContentTitle}
          description={props.page?.additionalContentDescription}
        />
      )}
      <div ref={bottomContentRef} data-test-id="bottom-subnavigation-area" />
      {hasSubNavigation &&
        !isContentCleaned(ContentKeys.BottomSubNavigationArea) &&
        props.page.bottomSubnavigationArea && (
          <BottomSubnavigationArea>
            <ContentList {...props.page.bottomSubnavigationArea} />
          </BottomSubnavigationArea>
        )}
      {props.articles &&
        props.articles.length > 0 &&
        showCmsContent &&
        !isContentCleaned(ContentKeys.BottomSubNavigationArea) && (
          <Inspiration>
            <Heading>{translate(t => t.inspiration.inspirationalArticlesHeading)}</Heading>
            <ArticleListing list={props.articles} cardBackgroundColor={isSvenssons ? undefined : white} />
          </Inspiration>
        )}
      {showCmsContent && <ContentList {...props.page.bottomContent} />}
    </Base>
  );
}

const BottomSubnavigationArea = styled.div({
  marginTop: t => t.spacing.l,
});

const Base = styled.div();

const Inspiration = styled.section(
  createMinMediumViewportDecorator({
    padding: { bottom: t => t.spacing.xxl },
  }),
  {
    backgroundColor: t => t.monochromeBrighter.color,
    padding: { top: t => t.spacing.l, bottom: t => t.spacing.xl },
  },
);

const Heading = styled.h2(
  headingDecorator,
  createMaxSmallViewportDecorator({
    fontSize: t => t.fontSize.m,
  }),
  {
    textAlign: 'center',
    marginBottom: t => t.spacing.l,
  },
);

const RetailMediaBannerAdHeader = styled(RetailMediaBannerAd, {});

const retailMediaBannerTopMarginDecorator = styled({
  marginTop: t => t.sharedSpacing.m,
});
